<!-- EditTagForm.vue -->
<script setup>
import { ref, onMounted, nextTick, onBeforeUnmount, computed } from "vue";
import { toTitleCase } from "@/utils/stringUtils";
import { useTagStore } from "@/stores/useTagStore";
import { useLinkedHighlightColors } from "@/composables/useLinkedHighlightColors";
import { useDraggableModal } from "@/composables/useDraggableModal";

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const emit = defineEmits(["close", "refresh"]);

const tagStore = useTagStore();

const obj = ref({
  tagId: 0,
  tagName: "",
  description: "",
  requiredBackground: false,
  specificColor: "#000000",
  specificBackground: "transparent",
  requiredUnderline: true,
  specificUnderline: "#000000",
});

// Régua de "temperatura" (0–100): modula a cor derivada da última escolhida -
// o fundo a partir do texto, ou o texto a partir do fundo.
const DEFAULT_TEMPERATURE = 75;
const temperature = ref(DEFAULT_TEMPERATURE);

// Segura a régua durante o carregamento inicial: a temperatura salva é
// atribuída no onMounted, e recalcular ali sobrescreveria as cores salvas.
const isLoading = ref(true);

const nameInput = ref(null);
const descriptionInput = ref(null);

// ─── Texto e fundo ligados nos dois sentidos ────────────────────────────────
// Escolher o texto recalcula o fundo (pastel) e escolher o fundo recalcula o
// texto; a régua de temperatura recalcula a cor que não foi a última escolhida.
// O sublinhado acompanha sempre a cor do texto. No carregamento (onMounted)
// nada é recalculado: as cores salvas são preservadas.
const { pickTextColor, pickBackgroundColor } = useLinkedHighlightColors({
  textColor: computed({
    get: () => obj.value.specificColor,
    set: (color) => {
      obj.value.specificColor = color;
      obj.value.specificUnderline = color;
    },
  }),
  backgroundColor: computed({
    get: () => obj.value.specificBackground,
    set: (color) => {
      obj.value.specificBackground = color;
    },
  }),
  temperature,
  isPaused: () => isLoading.value,
});

const DESCRIPTION_MAX = 1000;
const isSubmitting = ref(false);

const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

// Ao salvar com sucesso, esconde só o painel do modal; o toast segue sozinho.
const hideBackdropForSuccess = ref(false);

const isFormInvalid = computed(() => !obj.value.tagName.trim());

function enforceDescriptionMax() {
  if (!obj.value.description) return;
  if (obj.value.description.length > DESCRIPTION_MAX) {
    obj.value.description = obj.value.description.slice(0, DESCRIPTION_MAX);
  }
}

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();

  if (typeof onFinish === "function") {
    // Esconde o painel do modal já — o toast (num Teleport próprio) segue
    // visível sozinho até o onFinish (emit close) desmontar o componente.
    hideBackdropForSuccess.value = true;
  }

  setTimeout(() => {
    if (typeof onFinish === "function") {
      onFinish();
    }
  }, 2400);
}

const singleWordPreviewStyle = computed(() => ({
  backgroundColor: "transparent",
  color: obj.value.specificColor,
  borderRadius: "999px",
  padding: "0.15rem 0.15rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: "1.4",
  verticalAlign: "baseline",
  fontWeight: "600",
  border: "none",
}));

const multiWordPreviewStyle = computed(() => ({
  display: "inline-block",
  padding: "1px 3px",
  borderRadius: "0.5rem",
  fontWeight: "400",
  color: "var(--text-dark)",
  backgroundColor: obj.value.specificBackground,
  lineHeight: "1.05",
}));



async function atualizar() {
  if (isSubmitting.value) return;

  if (!obj.value.tagName || !obj.value.tagName.trim()) {
    showErrorToast("O nome da tag de nota é obrigatório.");
    isSubmitting.value = false;
    return;
  }

  try {
    isSubmitting.value = true;

    if (
      !obj.value.specificUnderline ||
      obj.value.specificUnderline === "initial"
    ) {
      obj.value.specificUnderline = "#000000";
    }

    obj.value.description = (obj.value.description || "").trim();
    enforceDescriptionMax();

    await tagStore.updateTag({ ...obj.value, specificTemperature: temperature.value });

    showSuccessToast("Alterações salvas com sucesso.", () => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    });
  } catch (error) {
    let userFriendlyMessage =
      "Ocorreu um erro desconhecido ao salvar as alterações.";
    const errorData = error?.response?.data || error;

    if (errorData && errorData.errors) {
      const messages = Object.values(errorData.errors).flat();
      if (messages.length > 0) {
        userFriendlyMessage = messages.join("\n");
      }
    } else if (errorData && errorData.message) {
      userFriendlyMessage = errorData.message;
    } else if (error.message && typeof error.message === "string") {
      userFriendlyMessage = error.message;
    }

    console.error("Erro detalhado da API ao salvar:", errorData);
    showErrorToast(userFriendlyMessage);
    isSubmitting.value = false;
  }
}

function closeModal() {
  if (!isSubmitting.value) {
    emit("close");
  }
}

function handleEscKey(event) {
  if (event.key === "Escape") {
    closeModal();
  }
}

onMounted(async () => {
  try {
    const current = await tagStore.getTagById(tagStore.idTag);

    obj.value = { ...obj.value, ...current };

    // Temperatura salva (fonte da verdade do fundo). Definida antes de liberar
    // o isLoading, para que a régua reabra na posição salva.
    temperature.value = current.specificTemperature ?? DEFAULT_TEMPERATURE;

    if (obj.value.specificBackground === "transparent") {
      obj.value.requiredBackground = false;
    } else {
      obj.value.requiredBackground = true;
    }

    obj.value.requiredUnderline = true;

    if (
      !obj.value.specificUnderline ||
      obj.value.specificUnderline === "transparent" ||
      obj.value.specificUnderline === "initial"
    ) {
      obj.value.specificUnderline = "#000000";
    }

    nextTick(() => {
      if (nameInput.value) {
        nameInput.value.focus();
        nameInput.value.select();
      }
      // Libera a régua só após o flush do carregamento, preservando as cores
      // salvas. A partir daqui, mexer na régua recalcula a cor derivada.
      isLoading.value = false;
    });

    document.addEventListener("keydown", handleEscKey);
  } catch (err) {
    console.error("Erro no onMounted:", err);
    showErrorToast("Erro ao carregar dados. Tente novamente.");
    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
  }
});
</script>

<template>
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-backdrop"
    @click.self="!isSubmitting"
  >
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Editar Tag de Nota</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg
            class="w-5 h-5"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="atualizar" novalidate class="modal-form">
        <div class="modal-body">
          <div class="form-content">
            <div class="form-group">
              <label for="name" class="form-label">Nome da Tag</label>
              <input
                ref="nameInput"
                type="text"
                id="name"
                v-model="obj.tagName"
                @blur="obj.tagName = toTitleCase(obj.tagName)"
                class="input-base"
                placeholder="Ex: Brasil Colônia, Religião e Espiritualidade, História Geral..."
                required
                @focus="$event.target.select()"
              />
            </div>

            <div class="form-group">
              <div class="label-row">
                <label for="description" class="form-label">
                  Descrição (opcional)
                </label>
                <span class="char-counter">
                  {{ (obj.description?.length || 0) }}/{{ DESCRIPTION_MAX }}
                </span>
              </div>

              <textarea
                ref="descriptionInput"
                id="description"
                v-model="obj.description"
                class="textarea-base"
                rows="3"
                :maxlength="DESCRIPTION_MAX"
                placeholder="Explique o sentido da tag, quando usar, e qualquer regra ou exemplo..."
                @input="enforceDescriptionMax"
              ></textarea>
            </div>

            <section class="shared-preview-section">
              <h4 class="section-title">Pré-visualização</h4>

              <div class="shared-preview-shell">
                <blockquote class="poem-blockquote">
                  <p class="poem-line">No silêncio da tarde, a luz repousa,</p>
                  <p class="poem-line">
                    sobre a janela antiga e sonhadora;
                  </p>
                  <p class="poem-line">
                    e o vento leva,
                    <span :style="singleWordPreviewStyle">manso</span>, a
                    memória,
                  </p>
                  <p class="poem-line">
                    de um
                    <span :style="multiWordPreviewStyle">
                      tempo feito de sombra
                    </span>
                    e rosa.
                  </p>
                </blockquote>
              </div>
            </section>

            <section class="style-section">
              <p class="style-intro">
                Defina como o destaque será exibido ao selecionar:
              </p>

              <div class="style-subsection">
                <h4 class="subsection-title">Palavras únicas</h4>

                <div class="pickers-only-section">
                  <div class="color-pickers">
                    <div class="color-picker-group inline-picker-group">
                      <label for="color-specific" class="form-label">
                        Cor do texto
                      </label>

                      <!-- ✅ PICKER REFATORADO:
                           o input nativo fica invisível e o círculo visível
                           é controlado por este wrapper, eliminando as arestas
                           quadradas internas do type="color". -->
                      <span
                        class="color-picker-shell"
                        :style="{ backgroundColor: obj.specificColor }"
                      >
                        <input
                          type="color"
                          id="color-specific"
                          :value="obj.specificColor"
                          @input="pickTextColor($event.target.value)"
                          class="color-input"
                          aria-label="Selecionar cor do texto"
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="style-section style-section-continued">
              <div class="style-subsection">
                <h4 class="subsection-title">Trechos com várias palavras</h4>

                <div class="pickers-only-section">
                  <div class="multi-word-row">
                    <div class="color-picker-group multi-word-bg-group">
                      <label for="color-background-multi" class="form-label">
                        Cor do fundo
                      </label>

                      <!-- Escolher o fundo recalcula a cor do texto. -->
                      <span
                        class="color-picker-shell"
                        :style="{ backgroundColor: obj.specificBackground }"
                      >
                        <input
                          type="color"
                          id="color-background-multi"
                          :value="obj.specificBackground"
                          @input="pickBackgroundColor($event.target.value)"
                          class="color-input"
                          aria-label="Selecionar cor do fundo"
                        />
                      </span>
                    </div>

                    <div class="temperature-group">
                      <div class="temperature-label-row">
                        <span class="temperature-value">{{ temperature }}</span>
                      </div>
                      <input
                        type="range"
                        id="temperature-range"
                        min="0"
                        max="100"
                        step="1"
                        v-model.number="temperature"
                        class="temperature-range"
                      />
                      <div class="temperature-scale-labels">
                        <span>Frio</span>
                        <span>Quente</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            @click="closeModal"
            class="btn btn-secondary"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isFormInvalid || isSubmitting"
          >
            <svg
              v-if="isSubmitting"
              class="w-5 h-5 mr-2 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>

            <svg
              v-else
              class="w-5 h-5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                clip-rule="evenodd"
              />
            </svg>

            <span>{{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[30000] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.char-counter {
  font-size: 0.78rem;
  color: var(--text-light);
  user-select: none;
}

.textarea-base {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.95rem;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
  resize: vertical;
  min-height: 88px;
}

.textarea-base:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.help-text {
  font-size: 0.8rem;
  color: var(--text-light);
  margin-top: 0.25rem;
}

.modal-panel {
  max-width: 36rem;
  height: 82vh;
  min-height: 690px;
  max-height: 690px;
}

.modal-form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.shared-preview-section,
.style-section {
  padding-top: 0.5rem;
}

.shared-preview-section {
  padding-top: 0.1rem;
}

.style-section-continued {
  padding-top: 0.15rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 0.25rem 0;
}

.style-intro {
  margin: 0 0 0.9rem 0;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: var(--text-light);
}

.style-subsection {
  padding-left: 0.1rem;
}

.subsection-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 0.35rem 0;
  line-height: 1.25;
}

.section-help {
  font-size: 0.82rem;
  color: var(--text-light);
  margin: 0 0 0.45rem 0;
}

.shared-preview-shell {
  width: 100%;
  min-height: 88px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 8px;
  font-size: 0.9rem;
  padding: 0.45rem 1.1rem 0.35rem;
  box-sizing: border-box;
  background: var(--bg-content);
}

.poem-blockquote {
  margin: 0;
  width: 100%;
  padding-left: 0.55rem;
  border-left: 4px solid #b39aff;
  font-style: italic;
  color: var(--text-dark);
}

.poem-line {
  margin: 0;
  line-height: 1.5;
  text-align: left;
}

.pickers-only-section {
  display: block;
}

.color-pickers {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.inline-dual-picker-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.inline-picker-left,
.inline-picker-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.inline-picker-left {
  flex: 1 1 auto;
  min-width: 0;
}

.inline-picker-right {
  flex: 0 0 auto;
  white-space: nowrap;
}

.color-picker-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: opacity 0.3s;
}

.toggle-control-group {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  width: auto;
  flex: 0 0 auto;
  cursor: pointer;
}

.toggle-label-text {
  white-space: nowrap;
  font-weight: 500;
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.toggle-switch {
  position: relative;
  display: block;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background-color: var(--neutral-400);
  transition: background-color 0.2s ease;
  flex: 0 0 auto;
}

.toggle-switch::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
}

.toggle-input:checked + .toggle-switch {
  background-color: var(--success);
}

.toggle-input:checked + .toggle-switch::after {
  transform: translateX(16px);
}

.color-picker-subgroup {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  transition: opacity 0.3s;
}

.color-picker-group.invisible,
.color-picker-subgroup.invisible {
  opacity: 0;
  pointer-events: none;
}

/* =========================================================
   ✅ PICKER CIRCULAR REFATORADO
   =========================================================
   O input[type="color"] nativo costuma renderizar internamente um swatch
   quadrado. Mesmo com border-radius, alguns navegadores deixam aparecer
   pequenas arestas internas. Por isso, o input fica invisível e ocupa todo
   o círculo; o elemento visível é .color-picker-shell.
   ========================================================= */

.color-picker-shell {
  position: relative;
  display: inline-flex;
  width: 1.5rem;
  height: 1.5rem;
  min-width: 1.5rem;
  min-height: 1.5rem;
  border-radius: 999px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  isolation: isolate;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.18);
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.color-picker-shell::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  z-index: 2;
}

.color-picker-shell:hover {
  transform: scale(1.05);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.1),
    0 2px 6px rgba(0, 0, 0, 0.22);
}

.color-picker-shell:focus-within {
  outline: none;
  box-shadow:
    0 0 0 2px var(--brand),
    0 1px 3px rgba(0, 0, 0, 0.18);
}

.color-input {
  position: absolute;
  inset: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.color-input::-webkit-color-swatch-wrapper {
  padding: 0;
  border: 0;
}

.color-input::-webkit-color-swatch {
  border: 0;
}

.color-input::-moz-color-swatch {
  border: 0;
}

/* Régua de temperatura */
.multi-word-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: 0.5rem;
}

.multi-word-bg-group {
  flex: 0 0 auto;
  justify-content: flex-start;
  gap: 0.6rem;
}

/* Coloca o picker logo após o label (à esquerda), sem espalhar até a borda.
   Definida após .color-picker-group para vencer o justify-content: space-between. */
.inline-picker-group {
  justify-content: flex-start;
  gap: 0.6rem;
}

.temperature-group {
  /* ~30% mais estreito que a largura que ocupava antes; alinhado à direita. */
  flex: 0 0 52%;
  min-width: 0;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.temperature-label-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.temperature-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-dark);
  font-variant-numeric: tabular-nums;
  min-width: 2ch;
  text-align: right;
}

.temperature-range {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(
    to right,
    #93c5fd 0%,
    var(--neutral-200) 50%,
    #fca35a 100%
  );
  cursor: pointer;
  outline: none;
}

.temperature-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--brand);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transition: transform 0.15s ease;
}

.temperature-range::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.temperature-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--surface);
  border: 2px solid var(--brand);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.temperature-range:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 3px;
}

.temperature-scale-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--text-light);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

</style>